import { useMemo } from 'react';
import useSWR from 'swr';
import { convertIPaymentSubscriptionToPaymentSubscription } from '~/common/converter/payment/payment';
import { useTellerSession } from '~/common/hooks/use-auth/use-teller-session';
import type { PaymentSubscriptionStatus } from '~/models/api-routes-response/payment-subscription-status';
import { PaymentPlatform } from '~/models/payment';
import {
  getLatestSubscription,
  listActiveSubscriptions,
} from '~/repositories/payment-repository';
const fetchPaymentSubscriptionStatus =
  async (): Promise<PaymentSubscriptionStatus> => {
    const activeSubscriptions = await listActiveSubscriptions();

    const isAdminSetVip =
      (activeSubscriptions &&
        activeSubscriptions.subscription_list?.some(
          (sub) =>
            sub.product?.platform === PaymentPlatform.PAYMENT_PLATFORM_TELLER
        )) ||
      false;

    if (isAdminSetVip) {
      return {
        hasActiveSubscription: false,
        subscriptionNeedsAttention: false,
      };
    }

    const hasActiveSubscription =
      (activeSubscriptions.subscription_list?.length ?? 0) > 0;
    let subscriptionNeedsAttention = false;

    if (!hasActiveSubscription) {
      const latestSubscription = await getLatestSubscription({
        platform: PaymentPlatform.PAYMENT_PLATFORM_UNSPECIFIED,
      }).then((response) => {
        if (!response) return;
        return convertIPaymentSubscriptionToPaymentSubscription(response);
      });

      if (!latestSubscription) {
        return {
          hasActiveSubscription: false,
          subscriptionNeedsAttention: false,
        };
      }

      const now = new Date();
      const chargeFailed = latestSubscription.chargeFailedAt.getFullYear() > 1;

      const resumeAtDate = latestSubscription?.resumeAt;
      const isInPause = resumeAtDate ? resumeAtDate > now : false;
      subscriptionNeedsAttention =
        latestSubscription && (isInPause || chargeFailed);
    }

    return {
      hasActiveSubscription,
      subscriptionNeedsAttention,
    };
  };

export const useFetchPaymentSubscriptionStatus = () => {
  const { session } = useTellerSession();

  const { data: paymentSubscriptionStatus, error } = useSWR(
    session?.uid && session?.tellerUser && !session.isAnonymous
      ? ['/me/get-payment-subscription-status']
      : null,
    () => fetchPaymentSubscriptionStatus(),
    {
      shouldRetryOnError: false,
    }
  );

  const loading = useMemo(
    () => !paymentSubscriptionStatus && !error,
    [error, paymentSubscriptionStatus]
  );

  return {
    loading,
    paymentSubscriptionStatus,
  };
};
